/**
 * Centralized processing messages for consistent UX across the chat.
 *
 * Design decisions:
 * - Generic messages that work for all agents and tools
 * - "Optimus" as the assistant identity
 * - Two phases: sending and processing
 */

export const PROCESSING_STATUS = {
  IDLE: 'idle',
  SENDING: 'sending',
  QUEUED: 'queued',
  PROCESSING: 'processing',
  COMPLETING: 'completing',
  ERROR: 'error',
} as const

export type ProcessingStatus =
  (typeof PROCESSING_STATUS)[keyof typeof PROCESSING_STATUS]

export const PROCESSING_MESSAGES = {
  // Phase 1: Sending message to backend
  SENDING: 'Processando...',

  // Phase 2: Task queued, waiting for worker
  QUEUED: 'Em andamento...',

  // Phase 3: Worker is processing
  PROCESSING: 'Analisando sua solicitação...',

  // Resuming a session that was processing
  RESUMING: 'Retomando conversa...',

  // Final phase before response
  COMPLETING: 'Concluindo análise...',

  // Error states
  ERROR_TIMEOUT:
    'A solicitação está demorando mais que o esperado. Tentando novamente...',
  ERROR_GENERIC: 'Algo deu errado. Por favor, tente novamente.',
  ERROR_PORTAL: 'Portal temporariamente indisponível.',
} as const

/**
 * Format elapsed time as mm:ss or hh:mm:ss
 */
export function formatElapsedTime(seconds: number): string {
  if (seconds < 0) return '00:00'

  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)

  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

/**
 * Format timestamp for message display (HH:mm)
 */
export function formatMessageTime(timestamp: string | Date): string {
  const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp
  return date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  })
}
